const config = {
  defaultMethod: "GET"
};

/**
 * Utility for fetching data from backend. Every request should be handled by this util.
 *
 * @param object data - data to use during fetch process. Parameters:
 * string url - URL address to call
 * [string] method - method to use during call (config.defaultMethod - GET - if not provided)
 * [string] token - token to use during call. If not provided, call will be with no token
 * [object] body - optional body to use during call. If provided, will be stringified automatically
 *
 * @param object callbacks - object of callbacks to use after fetch process. Callbacks will fire only if correctly
 * identified as proper one in terms of given fetch answer. Otherwise, no callback will fire. Callbacks are identified
 * from bottom. First exact http code (i.e. code_200), then code class (i.e. code_2xx) and then main callbacks
 * (i.e. success) if provided. Parameters:
 * [function] success - for <300 http answers
 * [function] error - for >=300 http answers
 * [function] code_2xx - for 200 <= Y < 300 http answers
 * [function] code_3xx, code_4xx, code_5xx - similar to code_2xx above
 * [function] code_200 - for 200 http answers
 * [function] code_abc - for 'abc' http answer (similar to code_200 above)
 *
 * @returns {Promise.<TResult>}
 */
export const handleFetch = (data, callbacks) => {
  let callback = null;

  return fetch(data.url, getFetchConfig(data))
    .then(response => {
      callback = getCallback(response.status, callbacks);
      return getCallbackArgument(response);
    })
    .then(callbackArgument => {
      if (!!callback) callback(callbackArgument);
    });
};

const getFetchConfig = data => {
  let fetchConfig = {
    credentials: "include", // pass cookies, for authentication
    mode: "cors", // enable cross-origin requests
    method: getMethodName(data.method || config.defaultMethod),
    cache: "no-store",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    }
  };

  if (!!data.token) fetchConfig.headers["X-CSRF-TOKEN"] = data.token;

  if (!!data.body) fetchConfig.body = JSON.stringify(data.body);

  return fetchConfig;
};

const getMethodName = method => {
  switch (method.toLowerCase()) {
    case "get":
    case "post":
    case "put":
    case "delete":
      return method.toUpperCase();
    default:
      return "";
  }
};

const getCallback = (status, callbacks) => {
  if (!!callbacks["code_" + status]) return callbacks["code_" + status];

  if (!!callbacks["code_" + Math.floor(status / 100) + "xx"])
    return callbacks["code_" + Math.floor(status / 100) + "xx"];

  if (status < 300 && !!callbacks["success"]) return callbacks["success"];

  if (status >= 300 && !!callbacks["error"]) return callbacks["error"];

  return null;
};

const getCallbackArgument = response => {
  if (response.status >= 200 && response.status < 300) return response.json();

  return null;
};
